import { LuArrowUpDown, LuChevronDown, LuChevronUp } from "react-icons/lu";
import { hasMinimumRole, ROLES } from "../../utils/roleUtils";

const COLUMNS = [
  { key: "id", name: "ID", sort_value: "id", className: "w-[7%]" },
  { key: "status", name: "Statusi", sort_value: "status", className: "w-[12%]" },
  { key: "title", name: "Titulli", sort_value: "title", className: "w-[28%]" },
  { key: "type", name: "Tipi", sort_value: "type", className: "w-[14%]" },
  { key: "start", name: "Fillimi", sort_value: "start_date", className: "w-[12%]" },
  { key: "end", name: "Mbarimi", sort_value: "end_date", className: "w-[12%]" },
  { key: "actions", name: "Veprimet", className: "w-[15%]" },
];

const STATUS_PILL = {
  active: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  canceled: "bg-red-50 text-red-700 ring-red-200",
  cancelled: "bg-red-50 text-red-700 ring-red-200",
  ended: "bg-gray-100 text-gray-700 ring-gray-200",
  draft: "bg-gray-100 text-gray-700 ring-gray-200",
};

const TYPE_LABEL = {
  lottery: "Lotari",
  cashback: "Cashback",
  business_draw: "Lotari për biznese",
};

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB");
  } catch {
    return "—";
  }
};

const STATUS_LABEL = {
  active: "Aktive",
  ended: "Pasive",
  canceled: "E anuluar",
  cancelled: "E anuluar",
  draft: "Draft",
};

const StatusPill = ({ status }) => {
  const key = status?.toLowerCase();
  const cls = STATUS_PILL[key] || "bg-gray-100 text-gray-700 ring-gray-200";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ring-1 ring-inset ${cls}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
      {STATUS_LABEL[key] || cap(status) || "—"}
    </span>
  );
};

const TypePill = ({ type }) => {
  const label = TYPE_LABEL[type] || cap(type?.replace(/_/g, " ")) || "—";
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200">
      {label}
    </span>
  );
};

const SkeletonRow = () => (
  <tr className="animate-pulse">
    <td className="px-6 py-4"><div className="h-3 w-8 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-5 w-16 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-48 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-5 w-16 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-20 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-20 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-12 bg-gray-200 rounded" /></td>
  </tr>
);

const SortIcon = ({ active, order }) => {
  if (!active)
    return (
      <LuArrowUpDown
        size={12}
        className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
      />
    );
  return order === "desc" ? (
    <LuChevronDown size={14} className="text-[var(--secondary-color)]" />
  ) : (
    <LuChevronUp size={14} className="text-[var(--secondary-color)]" />
  );
};

export default function GameTable({
  games,
  isLoading,
  params = {},
  onSort,
  onRowClick,
  onCancel,
}) {
  const canCancel = hasMinimumRole(ROLES.MANAGER);

  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.03)] overflow-hidden">
      {/* Desktop */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              {COLUMNS.map((col) => {
                const sortable = !!col.sort_value;
                const active = sortable && params.sort_by === col.sort_value;
                return (
                  <th
                    key={col.key}
                    onClick={sortable ? () => onSort?.(col.sort_value) : undefined}
                    className={`group ${col.className} text-left px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider ${
                      active ? "text-[var(--secondary-color)]" : "text-gray-500"
                    } ${sortable ? "cursor-pointer select-none hover:text-gray-900" : ""} transition-colors`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.name}
                      {sortable && <SortIcon active={active} order={params.sort_order} />}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (!games || games.length === 0)
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
              : games?.map((g) => {
                  const cancelable =
                    canCancel && g.status?.toLowerCase() === 'active';
                  return (
                    <tr
                      key={g.game_id}
                      onClick={() => onRowClick?.(g)}
                      className="group cursor-pointer transition-colors hover:bg-gray-50/70"
                    >
                      <td className="px-6 py-3.5 text-gray-500 tabular-nums text-xs">
                        #{g.game_id}
                      </td>
                      <td className="px-6 py-3.5">
                        <StatusPill status={g.status} />
                      </td>
                      <td className="px-6 py-3.5">
                        <span className="font-semibold text-gray-900 truncate block max-w-[420px]">
                          {g.title || "—"}
                        </span>
                      </td>
                      <td className="px-6 py-3.5">
                        <TypePill type={g.type} />
                      </td>
                      <td className="px-6 py-3.5 text-gray-700 whitespace-nowrap tabular-nums text-xs">
                        {formatDate(g.start_date)}
                      </td>
                      <td className="px-6 py-3.5 text-gray-700 whitespace-nowrap tabular-nums text-xs">
                        {formatDate(g.end_date)}
                      </td>
                      <td className="px-6 py-3.5">
                        {cancelable ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onCancel?.(g);
                            }}
                            className="inline-flex items-center rounded-md border border-gray-200 px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 hover:border-red-200 transition"
                          >
                            Anulo
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="md:hidden divide-y divide-gray-100">
        {isLoading && (!games || games.length === 0) ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse p-3 rounded-xl bg-gray-50 space-y-2">
                <div className="h-3 w-1/2 bg-gray-200 rounded" />
                <div className="h-2.5 w-3/4 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        ) : (
          games?.map((g) => {
            const cancelable =
              canCancel && g.status?.toLowerCase() !== "canceled" && g.status?.toLowerCase() !== "cancelled";
            return (
              <button
                key={g.game_id}
                type="button"
                onClick={() => onRowClick?.(g)}
                className="w-full text-left p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{g.title || "—"}</div>
                    <div className="text-xs text-gray-500 tabular-nums">#{g.game_id}</div>
                  </div>
                  <StatusPill status={g.status} />
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <TypePill type={g.type} />
                  <span className="text-xs text-gray-500 tabular-nums">
                    {formatDate(g.start_date)} → {formatDate(g.end_date)}
                  </span>
                </div>
                {cancelable && (
                  <div className="mt-3">
                    <span
                      role="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onCancel?.(g);
                      }}
                      className="inline-flex items-center rounded-md border border-gray-200 px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 hover:border-red-200 transition"
                    >
                      Anulo lojën
                    </span>
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
